import { chunk } from 'lodash-es'
import { type CustomerStore } from './CustomerStore'
import { type ParsedCustomerRow } from '@/features/iam/customers/customer-csv'
import { Id } from '@/infra/id'

/**
 * Result of a bulk customer submission.
 */
export interface BulkSubmissionResult {
  email: string
  givenName: string
  familyName: string
  success: boolean
  errorMessage?: string
}

const BATCH_SIZE = 25
const BATCH_DELAY_MS = 11000

/**
 * Creates customers in batches with a delay between batches to avoid rate
 * limiting, and reports the result for each customer.
 *
 * @param customers
 * @param customerStore
 * @param workspaceId
 * @param advisorUserPublicId
 */
export async function bulkCreateCustomers(
  customers: ParsedCustomerRow[],
  customerStore: Pick<CustomerStore, 'createCustomer'>,
  workspaceId: Id,
  advisorUserPublicId?: Id,
): Promise<BulkSubmissionResult[]> {
  // Asserted up front so that a missing workspace fails the whole submission
  // instead of being reported as a failure for every customer.
  const assertedWorkspaceId = Id.assert(workspaceId)
  const batches = chunk(customers, BATCH_SIZE)
  const results: BulkSubmissionResult[] = []

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i]

    const batchResults = await Promise.all(
      batch.map(async (customer) => {
        try {
          await customerStore.createCustomer({
            workspaceId: assertedWorkspaceId,
            email: customer.email,
            givenName: customer.givenName,
            familyName: customer.familyName,
            phoneNumber: customer.phoneNumber || undefined,
            advisorUserPublicId,
          })
          return {
            email: customer.email,
            givenName: customer.givenName,
            familyName: customer.familyName,
            success: true,
          }
        } catch (err) {
          const errorMessage =
            (err instanceof Error && err.message) || 'Unknown error'
          return {
            email: customer.email,
            givenName: customer.givenName,
            familyName: customer.familyName,
            success: false,
            errorMessage,
          }
        }
      }),
    )

    results.push(...batchResults)

    if (i < batches.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS))
    }
  }

  return results
}
